import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../index.css';

const CompanyForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const initialState = {
        companyName: '',
        type: 'shipper',
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        shipping: {
            addressType: 'shipping',
            addressLine: '',
            city: '',
            state: '',
            zip: ''
        },
        billing: {
            addressType: 'billing',
            addressLine: '',
            city: '',
            state: '',
            zip: ''
        }
    };
    const [company, setCompany] = useState(initialState);

    useEffect(() => {
        if (id) {
            fetchCompanyData();
        }
    }, [id]);

    const fetchCompanyData = async () => {
        try {
            const response = await axios.get(`/api/companies/${id}`);
            const data = response.data;
            const shippingAddress = data.addresses?.find(addr => addr.addressType === 'shipping') || {};
            const billingAddress = data.addresses?.find(addr => addr.addressType === 'billing') || {};
            
            setCompany({
                companyName: data.companyName || '',
                type: data.type || 'shipper',
                firstName: data.firstName || '',
                lastName: data.lastName || '',
                phone: formatPhoneNumber(data.phone) || '',
                email: data.email || '',
                shipping: {
                    addressType: 'shipping',
                    addressLine: shippingAddress.addressLine || '',
                    city: shippingAddress.city || '',
                    state: shippingAddress.state || '',
                    zip: shippingAddress.zip || ''
                },
                billing: {
                    addressType: 'billing',
                    addressLine: billingAddress.addressLine || '',
                    city: billingAddress.city || '',
                    state: billingAddress.state || '',
                    zip: billingAddress.zip || ''
                }
            });
        } catch (error) {
            console.error('Error fetching company:', error);
            alert('Error loading company data');
        }
    };

    const formatPhoneNumber = (value) => {
        if (!value) return value;
        
        // Remove all non-digits
        const phoneNumber = value.replace(/[^\d]/g, '');
        
        // Get length of the number
        const phoneNumberLength = phoneNumber.length;
        
        // Format based on length
        if (phoneNumberLength < 4) return phoneNumber;
        if (phoneNumberLength < 7) {
            return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
        }
        return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    };

    const handlePhoneChange = (e) => {
        const formattedPhone = formatPhoneNumber(e.target.value);
        setCompany(prev => ({ ...prev, phone: formattedPhone }));
    };

    const normalizePhoneForSubmit = (phone) => {
        return phone.replace(/[^\d]/g, '');
    };
    
    const handleAddressChange = (type, field, value) => {
        setCompany(prev => ({
            ...prev,
            [type]: {
                ...prev[type],
                [field]: value
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const submitData = {
                companyName: company.companyName,
                type: company.type,
                firstName: company.firstName,
                lastName: company.lastName,
                phone: normalizePhoneForSubmit(company.phone),
                email: company.email,
                addresses: [
                    {
                        addressType: 'shipping',
                        addressLine: company.shipping.addressLine,
                        city: company.shipping.city,
                        state: company.shipping.state,
                        zip: company.shipping.zip
                    }
                ]
            };

            if (company.billing.addressLine || company.billing.city || 
                company.billing.state || company.billing.zip) {
                submitData.addresses.push({
                    addressType: 'billing',
                    addressLine: company.billing.addressLine,
                    city: company.billing.city,
                    state: company.billing.state,
                    zip: company.billing.zip
                });
            }

            if (id) {
                await axios.put(`/api/companies/${id}`, submitData);
            } else {
                await axios.post('/api/companies', submitData);
            }
            alert(`Company ${id ? 'updated' : 'created'} successfully`);
            navigate('/companies');
        } catch (error) {
            console.error('Error saving company:', error);
            alert(error.response?.data?.message || 'Error saving company data');
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <div className="container mt-4">
            <h2>{id ? 'Edit' : 'New'} Company</h2>
            <form onSubmit={handleSubmit}>
                {/* Company Details */}
                <div className="row">
                    <div className="col-md-6 mb-3">
                        <label className="form-label">Company Name *</label>
                        <input
                            type="text"
                            className="form-control"
                            value={company.companyName}
                            onChange={(e) => setCompany({ ...company, companyName: e.target.value })}
                            required
                        />
                    </div>
                    <div className="col-md-6 mb-3">
                        <label className="form-label">Type *</label>
                        <select
                            className="form-control"
                            value={company.type}
                            onChange={(e) => setCompany({ ...company, type: e.target.value })}
                            required
                        >
                            <option value="shipper">Shipper</option>
                            <option value="carrier">Carrier</option>
                            <option value="consignee">Consignee</option>
                            <option value="grower">Grower</option>
                            <option value="other">Other</option>
                            <option value="vendor">Vendor</option>
                        </select>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-6 mb-3">
                        <label className="form-label">First Name</label>
                        <input
                            type="text"
                            className="form-control"
                            value={company.firstName || ''}
                            onChange={(e) => setCompany({ ...company, firstName: e.target.value })}
                        />
                    </div>
                    <div className="col-md-6 mb-3">
                        <label className="form-label">Last Name</label>
                        <input
                            type="text"
                            className="form-control"
                            value={company.lastName || ''}
                            onChange={(e) => setCompany({ ...company, lastName: e.target.value })}
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-6 mb-3">
                        <label className="form-label">Phone</label>
                        <input
                            type="tel"
                            className="form-control"
                            value={company.phone}
                            onChange={handlePhoneChange}
                            placeholder="(555) 555-5555"
                        />
                    </div>
                    <div className="col-md-6 mb-3">
                        <label className="form-label">Email</label>
                        <input
                            type="email"
                            className="form-control"
                            value={company.email || ''}
                            onChange={(e) => setCompany({ ...company, email: e.target.value })}
                        />
                    </div>
                </div>

                {/* Shipping Address */}
                <h3 className="mt-4">Shipping Address</h3>
                <div className="card mb-3">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-12 mb-3">
                                <label className="form-label">Street Address</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={company.shipping.addressLine}
                                    onChange={(e) => handleAddressChange('shipping', 'addressLine', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <label className="form-label">City</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={company.shipping.city}
                                    onChange={(e) => handleAddressChange('shipping', 'city', e.target.value)}
                                />
                            </div>
                            <div className="col-md-3 mb-3">
                                <label className="form-label">State</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={company.shipping.state}
                                    onChange={(e) => handleAddressChange('shipping', 'state', e.target.value)}
                                />
                            </div>
                            <div className="col-md-3 mb-3">
                                <label className="form-label">ZIP Code</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={company.shipping.zip}
                                    onChange={(e) => handleAddressChange('shipping', 'zip', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                    </div>

                    {/* Billing Address */}
                    <h3 className="mt-4">Billing Address</h3>
                    <div className="card mb-3">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <label className="form-label">Street Address</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={company.billing.addressLine}
                                        onChange={(e) => handleAddressChange('billing', 'addressLine', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">City</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={company.billing.city}
                                        onChange={(e) => handleAddressChange('billing', 'city', e.target.value)}
                                    />
                                </div>
                                <div className="col-md-3 mb-3">
                                    <label className="form-label">State</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={company.billing.state}
                                        onChange={(e) => handleAddressChange('billing', 'state', e.target.value)}
                                    />
                                </div>
                                <div className="col-md-3 mb-3">
                                    <label className="form-label">ZIP Code</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={company.billing.zip}
                                        onChange={(e) => handleAddressChange('billing', 'zip', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-4">
                        <div className="btn-group">
                    <button 
                        type="submit" 
                        className="btn btn-sm btn-outline-primary me-2"
                        disabled={loading}
                    >
                        {loading ? 'Saving...' : 'Save Company'}
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => navigate('/companies')}
                    >
                        Cancel
                    </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default CompanyForm;